import { Fragment, useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, BedDouble, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { getUnits, updateUnit, deleteUnit, createUnit } from "@/lib/api";

const UNIT_TYPES = [
  { value: "private", label: "Private Room" },
  { value: "apartment", label: "Apartment" },
  { value: "shared_bed", label: "Shared Bed" },
  { value: "private_shared_bath", label: "Private · shared bath" },
];

const TYPE_BADGE = {
  private: "bg-emerald-50 text-emerald-700 border-emerald-200",
  apartment: "bg-blue-50 text-blue-700 border-blue-200",
  shared_bed: "bg-amber-50 text-amber-700 border-amber-200",
  private_shared_bath: "bg-violet-50 text-violet-700 border-violet-200",
};

export default function Rooms() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingName, setEditingName] = useState(null); // unit id
  const [nameDraft, setNameDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // unit
  const [addOpen, setAddOpen] = useState(false);
  const emptyNew = { display_name: "", group: "", unit_type: "private" };
  const [newUnit, setNewUnit] = useState(emptyNew);

  const load = async () => {
    setLoading(true);
    try {
      const u = await getUnits();
      setUnits(u);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const u of units) (g[u.group] = g[u.group] || []).push(u);
    for (const k of Object.keys(g)) g[k].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    return g;
  }, [units]);

  const handleTypeChange = async (unit, newType) => {
    const prev = unit.unit_type;
    // optimistic update
    setUnits(list => list.map(u => u.id === unit.id ? { ...u, unit_type: newType } : u));
    try {
      await updateUnit(unit.id, { unit_type: newType });
      toast.success(`${unit.display_name}: typology updated`);
    } catch {
      setUnits(list => list.map(u => u.id === unit.id ? { ...u, unit_type: prev } : u));
      toast.error("Failed to update typology");
    }
  };

  const startEditName = (unit) => {
    setEditingName(unit.id);
    setNameDraft(unit.display_name);
  };

  const saveName = async (unit) => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === unit.display_name) {
      setEditingName(null);
      return;
    }
    try {
      await updateUnit(unit.id, { display_name: trimmed });
      setUnits(list => list.map(u => u.id === unit.id ? { ...u, display_name: trimmed } : u));
      setEditingName(null);
      toast.success("Name updated");
    } catch {
      toast.error("Failed to rename unit");
    }
  };

  const handleAddBed = async (group) => {
    const groupUnits = grouped[group] || [];
    if (!groupUnits.length) return;
    const baseline = groupUnits[0];
    // Next bed number = highest bed number + 1
    const bedNumbers = groupUnits
      .map(u => {
        const m = /bed\s*(\d+)/i.exec(u.name);
        return m ? parseInt(m[1], 10) : null;
      })
      .filter((n) => n !== null);
    const nextNum = bedNumbers.length ? Math.max(...bedNumbers) + 1 : groupUnits.length + 1;
    const name = `Bed ${nextNum}`;
    const display = `${group} - ${name}`;
    const maxSort = Math.max(...groupUnits.map(u => u.sort_order || 0), 0);
    try {
      const created = await createUnit({
        name,
        group,
        unit_type: baseline.unit_type,
        display_name: display,
        sort_order: maxSort + 1,
      });
      setUnits(list => [...list, created]);
      toast.success(`${display} added`);
    } catch {
      toast.error("Failed to add bed");
    }
  };

  const handleDelete = async (unit) => {
    try {
      await deleteUnit(unit.id);
      setUnits(list => list.filter(u => u.id !== unit.id));
      toast.success(`${unit.display_name} removed`);
      setConfirmDelete(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not remove unit";
      toast.error(msg);
      setConfirmDelete(null);
    }
  };

  const isSharedGroup = (group, units) => {
    // a group is "bed-based" if every unit in it is named "Bed N"
    return units.every(u => /^bed\s*\d+/i.test(u.name));
  };

  const handleCreateUnit = async () => {
    const display = newUnit.display_name.trim();
    const group = newUnit.group.trim();
    if (!display || !group) {
      toast.error("Group and display name are required");
      return;
    }
    // sort_order = max(existing) + 1
    const maxSort = units.reduce((m, u) => Math.max(m, u.sort_order || 0), 0);
    try {
      const created = await createUnit({
        name: display,
        group,
        unit_type: newUnit.unit_type,
        display_name: display,
        sort_order: maxSort + 1,
      });
      setUnits(list => [...list, created]);
      toast.success(`${display} added`);
      setAddOpen(false);
      setNewUnit(emptyNew);
    } catch {
      toast.error("Failed to create unit");
    }
  };

  const existingGroups = useMemo(
    () => Array.from(new Set(units.map(u => u.group))),
    [units],
  );

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Rooms & Typology</h1>
          <p className="text-sm text-slate-500 mt-1">Change the typology of rooms and manage the number of beds in shared groups</p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white h-11"
          data-testid="rooms-add-unit-btn"
        >
          <Plus size={16} /> New Room / Apartment
        </Button>
      </div>

      {loading && <div className="text-sm text-slate-400">Loading...</div>}

      <div className="space-y-5">
        {Object.entries(grouped).map(([group, list]) => {
          const shared = isSharedGroup(group, list);
          return (
            <Card key={group} className="shadow-none border-slate-200 overflow-hidden" data-testid={`rooms-group-${group}`}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50/60">
                <div>
                  <h2 className="font-heading font-semibold text-slate-900">{group}</h2>
                  <div className="text-xs text-slate-500 mt-0.5">{list.length} {shared ? "beds" : "unit(s)"}</div>
                </div>
                {shared && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAddBed(group)}
                    className="h-9"
                    data-testid={`rooms-add-bed-${group}`}
                  >
                    <Plus size={14} /> Add bed
                  </Button>
                )}
              </div>

              <div className="divide-y divide-slate-100">
                {list.map(u => (
                  <div key={u.id} className="flex items-center gap-3 px-5 py-3" data-testid={`rooms-unit-${u.id}`}>
                    <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                      <BedDouble size={16} className="text-slate-500" strokeWidth={1.75} />
                    </div>

                    {/* Display name, editable */}
                    <div className="flex-1 min-w-0">
                      {editingName === u.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            value={nameDraft}
                            onChange={e => setNameDraft(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && saveName(u)}
                            className="h-9 max-w-xs"
                            autoFocus
                            data-testid={`rooms-name-input-${u.id}`}
                          />
                          <Button size="sm" onClick={() => saveName(u)} className="h-9 bg-blue-600 hover:bg-blue-700 text-white" data-testid={`rooms-name-save-${u.id}`}>
                            <Check size={14} />
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEditName(u)}
                          className="group flex items-center gap-2 text-left hover:text-blue-700"
                          data-testid={`rooms-name-${u.id}`}
                        >
                          <div className="font-medium text-slate-900 truncate">{u.display_name}</div>
                          <Pencil size={12} className="text-slate-300 group-hover:text-blue-500 transition-colors" />
                        </button>
                      )}
                      <div className="text-xs text-slate-400 mt-0.5">Internal name: {u.name}</div>
                    </div>

                    {/* Typology selector */}
                    <Select value={u.unit_type} onValueChange={(v) => handleTypeChange(u, v)}>
                      <SelectTrigger className={`w-[200px] h-9 text-xs border ${TYPE_BADGE[u.unit_type] || "border-slate-200"}`} data-testid={`rooms-type-select-${u.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNIT_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setConfirmDelete(u)}
                      className="h-9 w-9 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                      title="Remove unit"
                      data-testid={`rooms-delete-${u.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="rooms-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.display_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Units with existing reservations cannot be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="rooms-delete-confirm-btn"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) { setAddOpen(false); setNewUnit(emptyNew); } }}>
        <DialogContent className="max-w-md" data-testid="rooms-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New Room / Apartment</DialogTitle>
            <DialogDescription className="sr-only">Create a new bookable unit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Display name</Label>
              <Input
                value={newUnit.display_name}
                onChange={(e) => setNewUnit(v => ({ ...v, display_name: e.target.value }))}
                placeholder="e.g. Room 7, Apartment 3, Suite Laranja"
                className="mt-1 h-11"
                autoFocus
                data-testid="rooms-new-display-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Group</Label>
              <Input
                value={newUnit.group}
                onChange={(e) => setNewUnit(v => ({ ...v, group: e.target.value }))}
                placeholder="Type existing or new group name..."
                list="rooms-group-suggestions"
                className="mt-1 h-11"
                data-testid="rooms-new-group"
              />
              <datalist id="rooms-group-suggestions">
                {existingGroups.map(g => <option key={g} value={g} />)}
              </datalist>
              <div className="text-[11px] text-slate-400 mt-1">Existing groups: {existingGroups.join(" · ") || "—"}</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Typology</Label>
              <Select
                value={newUnit.unit_type}
                onValueChange={(v) => setNewUnit(u => ({ ...u, unit_type: v }))}
              >
                <SelectTrigger className="mt-1 h-11" data-testid="rooms-new-typology">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddOpen(false); setNewUnit(emptyNew); }}>Cancel</Button>
            <Button
              onClick={handleCreateUnit}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="rooms-new-save-btn"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
